/**
 * apps/web :: lib/exam-api
 *
 * Typed wrappers over the `/api/exam/*` surface shipped in PR #120
 * (ROADS-304). Mirrors the fetch-wrapper conventions used by
 * practice-api.ts — every call is credentialed, 4xx/5xx bubble up as
 * `ApiError` unless they have a recognised structured body, in which case
 * we surface a typed error subclass the page can switch on.
 *
 * Wire-shape asymmetry (worth pinning once — ExamDomain.kt is the source
 * of truth):
 *
 *   - ExamQuestionForClient mirrors PracticeQuestionForClient: compound
 *     fields carry @SerialName snake_case (`state_code`, `question_text`,
 *     `handbook_section`) and single-word fields stay camelCase by
 *     kotlinx default (`id`, `locale`, `category`, `choices`, `difficulty`).
 *     We preserve that on-the-wire shape verbatim in TS so the existing
 *     QuestionCard / ChoiceList (consumed as-is from /practice) renders
 *     without translation.
 *   - StartExamResponse explicitly @SerialNames `sessionId`, `startedAtMs`,
 *     `deadlineMs` to camelCase. The remaining fields default to camelCase.
 *   - SubmitExamRequest / SubmitExamResponse / ExamHistoryEntry /
 *     ExamConfigResponse carry NO @SerialName annotations — pure camelCase
 *     on the wire.
 *   - ExamSpec (shared-core, `cx.humane.roadbridge.shared.exam.ExamSpec`)
 *     serialises its four fields as snake_case (`question_count`,
 *     `time_limit_seconds`, `pass_percent`, `retry_cooldown_minutes`).
 *     Every response with an embedded `spec` is therefore mixed-shape.
 *
 * We translate `spec` (snake → camel) + the review entries (so the TS
 * surface is consistent camelCase) once inside this module. The question
 * payload is preserved as-is because QuestionCard already consumes the
 * snake_case shape from practice-api.
 *
 * Typed errors:
 *   - ExamBankTooSmallError     — 409 `{status:"bank_too_small", ...}`
 *   - ExamSessionInProgressError — 409 `{status:"session_in_progress", ...}`
 *   - ExamCooldownError          — 429 `{status:"cooldown", ...}`
 *
 * Any other non-2xx flows through as `ApiError` unchanged.
 */
import { ApiError, apiFetch } from "@/lib/api";
import type { PracticeQuestionForClient } from "@/lib/practice-api";

// ---------------------------------------------------------------------------
// Typed error classes
// ---------------------------------------------------------------------------

/**
 * Thrown from [startExam] on 409 `bank_too_small` — the state's question
 * bank has fewer questions than spec.questionCount. The page uses
 * `available` + `required` to render a specific "bank isn't ready" copy.
 */
export class ExamBankTooSmallError extends Error {
  public readonly available: number;
  public readonly required: number;
  constructor(available: number, required: number) {
    super(`Question bank too small: ${available}/${required}`);
    this.name = "ExamBankTooSmallError";
    this.available = available;
    this.required = required;
  }
}

/**
 * Thrown from [startExam] on 409 `session_in_progress`. The caller can
 * resume by re-fetching the known session or wait it out.
 */
export class ExamSessionInProgressError extends Error {
  public readonly sessionId: string;
  public readonly deadlineMs: number;
  constructor(sessionId: string, deadlineMs: number) {
    super(`Exam session already in progress: ${sessionId}`);
    this.name = "ExamSessionInProgressError";
    this.sessionId = sessionId;
    this.deadlineMs = deadlineMs;
  }
}

/**
 * Thrown from [startExam] on 429 cooldown — the user failed a recent
 * attempt and must wait until `retryAvailableAtMs`.
 */
export class ExamCooldownError extends Error {
  public readonly retryAvailableAtMs: number;
  constructor(retryAvailableAtMs: number) {
    super(`Exam cooldown until ${retryAvailableAtMs}`);
    this.name = "ExamCooldownError";
    this.retryAvailableAtMs = retryAvailableAtMs;
  }
}

/**
 * ROADS-450 — thrown from [getAttemptDetail] on 404. The page renders
 * the existing "we couldn't find that attempt" copy.
 */
export class ExamAttemptNotFoundError extends Error {
  public readonly attemptId: string;
  constructor(attemptId: string) {
    super(`Exam attempt not found: ${attemptId}`);
    this.name = "ExamAttemptNotFoundError";
    this.attemptId = attemptId;
  }
}

/**
 * ROADS-450 — thrown from [getAttemptDetail] on 403. The attempt id
 * resolves but belongs to a different account. The page renders a
 * friendly "this attempt belongs to a different account" surface
 * (NOT the row-level "not found" — we want the user to know the URL
 * is meaningful, just not theirs).
 */
export class ExamAttemptForbiddenError extends Error {
  public readonly attemptId: string;
  constructor(attemptId: string) {
    super(`Exam attempt belongs to a different user: ${attemptId}`);
    this.name = "ExamAttemptForbiddenError";
    this.attemptId = attemptId;
  }
}

// ---------------------------------------------------------------------------
// Client-facing TS types
// ---------------------------------------------------------------------------

/** Client-safe exam question — identical shape to PracticeQuestionForClient. */
export type ExamQuestionForClient = PracticeQuestionForClient;

/** ExamSpec in client-camelCase (wire is snake_case — translated in this lib). */
export interface ExamSpec {
  questionCount: number;
  timeLimitSeconds: number;
  passPercent: number;
  retryCooldownMinutes: number;
}

/** Response from POST /api/exam/start. */
export interface StartExamResponse {
  sessionId: string;
  questions: ExamQuestionForClient[];
  startedAtMs: number;
  deadlineMs: number;
  spec: ExamSpec;
}

/** POST /api/exam/submit request payload. */
export interface SubmitExamPayload {
  sessionId: string;
  answers: Array<{ questionId: string; chosenChoiceId: string | null }>;
}

/** One review entry in the SubmitExamResponse.review list. */
export interface ExamReviewEntry {
  questionId: string;
  chosenChoiceId: string | null;
  correctChoiceId: string;
  isCorrect: boolean;
  explanation: string;
  handbookSection: string | null;
}

/**
 * ROADS-430: per-category accuracy slice computed at submit time.
 * Wire shape mirrors `cx.humane.roadbridge.shared.exam.ExamSectionBreakdown`.
 *
 *   - category — `QuestionCategory.name` (e.g. "RIGHT_OF_WAY"). The UI
 *     translates via the `exam.results.section.<lower-snake>` key.
 *   - answered — # questions in this category included in the attempt.
 *   - correct  — # the user got right.
 *   - accuracy — correct / answered, in [0, 1].
 */
export interface ExamSectionBreakdown {
  category: string;
  answered: number;
  correct: number;
  accuracy: number;
}

/**
 * ROADS-430: a category that qualified as "weak" per the diagnostic
 * (accuracy < 0.70 with answered ≥ 3). Sorted by the server, lowest
 * accuracy first.
 */
export interface ExamWeakArea {
  category: string;
  answered: number;
  correct: number;
  accuracy: number;
}

/** Response from POST /api/exam/submit. */
export interface SubmitExamResponse {
  attemptId: string;
  sessionId: string;
  passed: boolean;
  correctCount: number;
  questionCount: number;
  passPercent: number;
  /** Null when the user passed (no cooldown applies). */
  retryAvailableAtMs: number | null;
  review: ExamReviewEntry[];
  /** ROADS-430. Empty when no questions had category metadata available. */
  perSectionBreakdown: ExamSectionBreakdown[];
  /** ROADS-430. Empty when no category qualified as "weak". */
  weakAreas: ExamWeakArea[];
}

/** One row in the ExamHistoryResponse.attempts list. */
export interface ExamHistoryEntry {
  attemptId: string;
  sessionId: string;
  submittedAtMs: number;
  passed: boolean;
  correctCount: number;
  questionCount: number;
  passPercent: number;
  timeSpentMs: number;
  attemptNumber: number;
}

/** Response from GET /api/exam/history. */
export interface ExamHistoryResponse {
  stateCode: string;
  attempts: ExamHistoryEntry[];
}

/** Response from GET /api/exam/config. */
export interface ExamConfigResponse {
  stateCode: string;
  spec: ExamSpec;
  bankAvailable: number;
  bankSufficient: boolean;
}

/**
 * ROADS-450 — full attempt detail returned by GET /api/exam-attempts/{id}.
 *
 * Wire-shape mirrors the rest of the kmp-shared-core exam DTOs (snake_case
 * for compound fields). We normalise into camelCase before handing the
 * page the type so component code never has to know about the
 * @SerialName policy. The lib does NOT include the per-question
 * `answers` array — Hard Rule #4 enforced at the type boundary.
 */
export interface ExamAttemptDetail {
  id: string;
  userId: string;
  /** Source `ExamSession.id`. Named `exam_id` on the wire per ROADS-450. */
  examId: string;
  stateCode: string;
  /** "class_c" | "cdl" — defaults to class_c on pre-ROADS-430 attempts. */
  pillar: string;
  /** Number of questions the user got right (== correct_count on the row). */
  score: number;
  questionCount: number;
  /** Integer pass threshold (0..100) snapshotted at submit time. */
  passThreshold: number;
  passed: boolean;
  /** Wall-clock start (ms epoch). */
  startedAt: number;
  /** Wall-clock submit (ms epoch). */
  completedAt: number;
  perSectionBreakdown: ExamSectionBreakdown[];
  /** Computed by the backend; empty when no category qualifies as weak. */
  weakAreas: ExamWeakArea[];
}

// ---------------------------------------------------------------------------
// Internal wire shapes (exported only for tests to share constants)
// ---------------------------------------------------------------------------

interface SpecWire {
  question_count: number;
  time_limit_seconds: number;
  pass_percent: number;
  retry_cooldown_minutes: number;
}

function parseSpec(wire: SpecWire): ExamSpec {
  return {
    questionCount: wire.question_count,
    timeLimitSeconds: wire.time_limit_seconds,
    passPercent: wire.pass_percent,
    retryCooldownMinutes: wire.retry_cooldown_minutes,
  };
}

function isBody<T extends Record<string, unknown>>(body: unknown): body is T {
  return typeof body === "object" && body !== null;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** GET /api/exam/config?stateCode=CA — spec + bank availability. */
export async function getExamConfig(
  stateCode: string,
): Promise<ExamConfigResponse> {
  const params = new URLSearchParams({ stateCode });
  const wire = await apiFetch<{
    stateCode: string;
    spec: SpecWire;
    bankAvailable: number;
    bankSufficient: boolean;
  }>(`/api/exam/config?${params.toString()}`);
  return {
    stateCode: wire.stateCode,
    spec: parseSpec(wire.spec),
    bankAvailable: wire.bankAvailable,
    bankSufficient: wire.bankSufficient,
  };
}

/**
 * POST /api/exam/start — create a new session. On structured 409/429
 * bodies we throw a typed error so the page can route to the specific
 * user-facing state (cooldown banner, bank-not-ready banner, resume
 * prompt). Everything else bubbles up as ApiError.
 */
export async function startExam(stateCode: string): Promise<StartExamResponse> {
  try {
    const wire = await apiFetch<{
      sessionId: string;
      questions: ExamQuestionForClient[];
      startedAtMs: number;
      deadlineMs: number;
      spec: SpecWire;
    }>("/api/exam/start", {
      method: "POST",
      body: JSON.stringify({ stateCode }),
    });
    return {
      sessionId: wire.sessionId,
      questions: wire.questions,
      startedAtMs: wire.startedAtMs,
      deadlineMs: wire.deadlineMs,
      spec: parseSpec(wire.spec),
    };
  } catch (err) {
    if (err instanceof ApiError && isBody<{ status?: string }>(err.body)) {
      if (err.status === 409 && err.body.status === "bank_too_small") {
        const b = err.body as {
          available?: number;
          required?: number;
        };
        throw new ExamBankTooSmallError(b.available ?? 0, b.required ?? 0);
      }
      if (err.status === 409 && err.body.status === "session_in_progress") {
        const b = err.body as {
          sessionId?: string;
          deadlineMs?: number;
        };
        throw new ExamSessionInProgressError(
          b.sessionId ?? "",
          b.deadlineMs ?? 0,
        );
      }
      if (err.status === 429 && err.body.status === "cooldown") {
        const b = err.body as { retryAvailableAtMs?: number };
        throw new ExamCooldownError(b.retryAvailableAtMs ?? 0);
      }
    }
    throw err;
  }
}

/** POST /api/exam/submit — score a session. */
export async function submitExam(
  payload: SubmitExamPayload,
): Promise<SubmitExamResponse> {
  const wire = await apiFetch<{
    attemptId: string;
    sessionId: string;
    passed: boolean;
    correctCount: number;
    questionCount: number;
    passPercent: number;
    retryAvailableAtMs: number | null;
    review: Array<{
      questionId: string;
      chosenChoiceId: string | null;
      correctChoiceId: string;
      isCorrect: boolean;
      explanation: string;
      handbookSection: string | null;
    }>;
    // ROADS-430. Optional in the wire so older backends still parse.
    perSectionBreakdown?: ExamSectionBreakdown[];
    weakAreas?: ExamWeakArea[];
  }>("/api/exam/submit", {
    method: "POST",
    body: JSON.stringify({
      sessionId: payload.sessionId,
      answers: payload.answers.map((a) => ({
        questionId: a.questionId,
        chosenChoiceId: a.chosenChoiceId,
      })),
    }),
  });
  return {
    attemptId: wire.attemptId,
    sessionId: wire.sessionId,
    passed: wire.passed,
    correctCount: wire.correctCount,
    questionCount: wire.questionCount,
    passPercent: wire.passPercent,
    retryAvailableAtMs: wire.retryAvailableAtMs,
    review: wire.review.map((r) => ({
      questionId: r.questionId,
      chosenChoiceId: r.chosenChoiceId ?? null,
      correctChoiceId: r.correctChoiceId,
      isCorrect: r.isCorrect,
      explanation: r.explanation,
      handbookSection: r.handbookSection ?? null,
    })),
    perSectionBreakdown: wire.perSectionBreakdown ?? [],
    weakAreas: wire.weakAreas ?? [],
  };
}

/** GET /api/exam/history?stateCode=CA&limit=10 — recent attempts. */
export async function getExamHistory(
  stateCode: string,
  limit = 10,
): Promise<ExamHistoryResponse> {
  const params = new URLSearchParams({
    stateCode,
    limit: String(limit),
  });
  return apiFetch<ExamHistoryResponse>(
    `/api/exam/history?${params.toString()}`,
  );
}

/**
 * ROADS-450 — GET /api/exam-attempts/{id}. Powers the deep-link /
 * page-refresh hydration of `/exam/history/:attemptId`. The in-session
 * row click still hands the row through router state for zero-latency
 * rendering; this endpoint is the durable fallback.
 *
 * The endpoint returns snake_case on the wire to match the rest of
 * kmp-shared-core exam DTOs. We normalise to camelCase here so the
 * page can stay shape-agnostic.
 *
 * Errors:
 *   - 403 → ExamAttemptForbiddenError (attempt belongs to another user)
 *   - 404 → ExamAttemptNotFoundError
 *   - 5xx / network → ApiError (page renders the generic error surface)
 */
export async function getAttemptDetail(
  attemptId: string,
): Promise<ExamAttemptDetail> {
  // URL-encode defensively. Real ids are UUIDs, but the component layer
  // pulls the id straight off the URL — never trust it not to contain
  // reserved chars or path traversal payloads.
  const encoded = encodeURIComponent(attemptId);
  try {
    const wire = await apiFetch<{
      id: string;
      user_id: string;
      exam_id: string;
      state_code: string;
      pillar: string;
      score: number;
      question_count: number;
      pass_threshold: number;
      passed: boolean;
      started_at: number;
      completed_at: number;
      per_section_breakdown?: ExamSectionBreakdown[];
      weak_areas?: ExamWeakArea[];
    }>(`/api/exam-attempts/${encoded}`);
    return {
      id: wire.id,
      userId: wire.user_id,
      examId: wire.exam_id,
      stateCode: wire.state_code,
      pillar: wire.pillar,
      score: wire.score,
      questionCount: wire.question_count,
      passThreshold: wire.pass_threshold,
      passed: wire.passed,
      startedAt: wire.started_at,
      completedAt: wire.completed_at,
      perSectionBreakdown: wire.per_section_breakdown ?? [],
      weakAreas: wire.weak_areas ?? [],
    };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 404) {
        throw new ExamAttemptNotFoundError(attemptId);
      }
      if (err.status === 403) {
        throw new ExamAttemptForbiddenError(attemptId);
      }
    }
    throw err;
  }
}
